import { __decorate } from "tslib";
import { attr, css, cssPartial, } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { foundationGridLayoutStyles as styles } from './grid-layout.styles';
import { foundationGridLayoutTemplate as template } from './grid-layout.template';
import { GridMediaQueriesEnum } from './grid-layout.types';
export const foundationGridLayoutShadowOptions = undefined;
export const defaultGridLayoutConfig = {};
class ResponsiveGridBehavior {
    constructor(query, cols, rows, width = '1fr', height = '1fr') {
        this.listenerCache = new WeakMap();
        this.query = window.matchMedia(query);
        const colPartial = cols && cssPartial `grid-template-columns: repeat(${cols}, ${width});`;
        const rowPartial = rows && cssPartial `grid-template-rows: repeat(${rows}, ${height});`;
        this.styles = css `
      slot {
        ${rowPartial || ''}
        ${colPartial || ''}
      }
    `;
    }
    bind(source) {
        let attached = false;
        const listener = (event) => {
            const { matches } = event;
            if (matches && !attached) {
                source.$fastController.addStyles(this.styles);
                attached = true;
            }
            else if (!matches && attached) {
                source.$fastController.removeStyles(this.styles);
                attached = false;
            }
        };
        listener({ matches: this.query.matches });
        this.query.addEventListener('change', listener);
        this.listenerCache.set(source, listener);
    }
    unbind(source) {
        const listener = this.listenerCache.get(source);
        if (listener) {
            this.query.removeEventListener('change', listener);
            this.listenerCache.delete(source);
        }
        source.$fastController.removeStyles(this.styles);
    }
}
/**
 * @tagname %%prefix%%-grid-layout
 */
export class GridLayout extends FoundationElement {
    constructor() {
        super(...arguments);
        this.behaviors = [];
    }
    connectedCallback() {
        super.connectedCallback();
        this.behaviors = Object.keys(GridMediaQueriesEnum)
            .map((bp) => {
            if (!this[`col-${bp}-count`] && !this[`row-${bp}-count`]) {
                return;
            }
            return new ResponsiveGridBehavior(GridMediaQueriesEnum[bp], this[`col-${bp}-count`], this[`row-${bp}-count`], this[`col-${bp}-width`], this[`row-${bp}-height`]);
        })
            .filter((x) => !!x);
        const rowPartial = this['row-count'] &&
            cssPartial `grid-template-rows: repeat(${this['row-count']}, ${this['row-height'] || '1fr'});`;
        const colPartial = this['col-count'] &&
            cssPartial `grid-template-columns: repeat(${this['col-count']}, ${this['col-width'] || '1fr'});`;
        this.baseStyles = css `
      slot {
        ${rowPartial || ''}
        ${colPartial || ''}
      }
    `;
        this.$fastController.addStyles(this.baseStyles);
        this.$fastController.addBehaviors(this.behaviors);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.$fastController.removeBehaviors(this.behaviors);
        this.$fastController.removeStyles(this.baseStyles);
    }
}
__decorate([
    attr
], GridLayout.prototype, 'col-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-width', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-lg-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-lg-width', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-md-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-md-width', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-sm-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'col-sm-width', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-height', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-lg-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-lg-height', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-md-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-md-height', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-sm-count', void 0);
__decorate([
    attr
], GridLayout.prototype, 'row-sm-height', void 0);
/**
 * The Foundation Grid Layout
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-grid-layout\>
 */
export const foundationGridLayout = GridLayout.compose(Object.assign({ baseName: 'grid-layout', styles,
    template, shadowOptions: foundationGridLayoutShadowOptions }, defaultGridLayoutConfig));
